/**
 * @file add-primary-categories.tsx
 * @deprecated Copied to app/modules/procurement/pages/master/category/add-primary-categories.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/master/category/add-primary-categories
 * @description Renders the "Add primary category" page, allowing users to add one or more primary categories with
 * names, images, and tobacco-related flags. Accessible via the "Add primary category" button on the AllCategories page.
 * Contains form validation and calls the API to save new categories.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Missing auditLogMessage: pass generateLogMessageForCategory via
 *   categoryJSON.addPrimaryCategory({ ..., auditLogMessage }) like uprocure-admin.
 * @todo Unused import: generateLogMessageForCategory is imported but never used.
 * @todo Remove duplicate h1 when PageTitle is used on the route wrapper.
 */

import clsx from "clsx";
import React, { FC, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import UploadImage from "../.../../../../../../_admin/assets/media/svg_uTrade/add-img.svg";
import { fileValidation } from "../../../../../Global/fileValidation";
import { success } from "../../../../../Global/toast";
import cross from "../../../../../_admin/assets/media/svg_uTrade/cross-red.svg";
import Close from "../../../../../_admin/assets/media/svg_uTrade/cross-rounded-gray.svg";
import APICallService from "../../../../../api/apiCallService";
import { master } from "../../../../../api/apiEndPoints";
import { categoryJSON } from "../../../../../api/apiJSON/master";
import { masterToast } from "../../../../../utils/toast";
import { Add, Categories } from "../../../../../utils/constants";
import Method from "../../../../../utils/methods";
import { useAuth } from "../../../auth";
import { getKey } from "../../../../../Global/history";
import { warehouse as warehouseStoreString } from "../../../../../utils/storeString";
import { useWarehouse } from "../../../../context/WarehouseContext";
import { CustomToggleSwitch } from '../../../../custom/toggle-switch/CustomToggleSwitch';

const AddPrimaryCategory: FC = () => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [primaryCategory, setPrimaryCategory] = useState<
    {
      categoryName: string;
      categoryImage: string;
      categoryImageReader: any;
      isTobacco: boolean;
      isPharmacyCategory: boolean;
    }[]
  >([
    {
      categoryName: "",
      categoryImage: "",
      categoryImageReader: "",
      isTobacco: false,
      isPharmacyCategory: false,
    },
  ]);
  const [validation, setValidation] = useState<
    {
      categoryName: boolean;
      categoryImage: boolean;
    }[]
  >([
    {
      categoryName: false,
      categoryImage: false,
    },
  ]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    (async () => {
      if (!Method.hasPermission(Categories, Add, currentUser)) {
        return window.history.back();
      }
    })();
  }, []);
  const handleAddMore = () => {
    if (!loading) {
      let temp = [...primaryCategory];
      let validateTemp = [...validation];
      let length = temp.length - 1;
      if (!temp[length].categoryName && !temp[length].categoryImage) {
        validateTemp[length].categoryName = true;
        validateTemp[length].categoryImage = true;
      } else if (!temp[length].categoryName) {
        validateTemp[length].categoryName = true;
      } else if (!temp[length].categoryImage) {
        validateTemp[length].categoryImage = true;
      } else {
        const allTrue = validateTemp.every(
          (obj) => obj.categoryName === false && obj.categoryImage === false,
        );
        if (allTrue) {
          validateTemp.push({
            categoryName: false,
            categoryImage: false,
          });
          temp.push({
            categoryName: "",
            categoryImage: "",
            categoryImageReader: "",
            isTobacco: false,
            isPharmacyCategory: false,
          });
          setPrimaryCategory(temp);
        }
      }
      setValidation(validateTemp);
    }
  };
  const handleRemove = async (e: any, index: any) => {
    let temp = [...primaryCategory];
    let validateTemp = [...validation];
    validateTemp.splice(index, 1);
    temp.splice(index, 1);
    setValidation(validateTemp);
    setPrimaryCategory(temp);
  };
  const handleCategoryName = (value: any, index: any) => {
    let temp = [...primaryCategory];
    let validateTemp = [...validation];
    temp[index].categoryName = value.trimStart();
    if (value.trimStart().length > 0) {
      validateTemp[index].categoryName = false;
    } else {
      validateTemp[index].categoryName = true;
    }
    setPrimaryCategory(temp);
    setValidation(validateTemp);
  };
  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: any,
  ) => {
    const selectedFiles = event.target.files;
    let temp = [...primaryCategory];
    let validateTemp = [...validation];
    if (!selectedFiles) return;
    else {
      if (fileValidation(selectedFiles?.[0])) {
        validateTemp[index].categoryImage = false;
        temp[index].categoryImage = URL.createObjectURL(selectedFiles?.[0]);
        temp[index].categoryImageReader = selectedFiles?.[0];
        setPrimaryCategory(temp);
      }
    }
    setValidation(validateTemp);
  };
  const handleImageDiscard = (index: any) => {
    let temp = [...primaryCategory];
    let validateTemp = [...validation];
    validateTemp[index].categoryImage = true;
    temp[index].categoryImage = "";
    temp[index].categoryImageReader = {};
    setPrimaryCategory(temp);
    setValidation(validateTemp);
  };
  const handleTobacco = (value: any, index: any) => {
    let temp = [...primaryCategory];
    temp[index].isTobacco = !temp[index].isTobacco;
    setPrimaryCategory(temp);
  };
  const handlePharmacyToggle = (value: boolean, index: number) => {
    let temp = [...primaryCategory];
    temp[index].isPharmacyCategory = value;
    setPrimaryCategory(temp);
  };
  const handleSaveCategory = async () => {
    let temp = [...primaryCategory];
    let validateTemp = [...validation];
    let length = temp.length - 1;
    if (!temp[length].categoryName && !temp[length].categoryImage) {
      validateTemp[length].categoryName = true;
      validateTemp[length].categoryImage = true;
    } else if (!temp[length].categoryName) {
      validateTemp[length].categoryName = true;
    } else if (!temp[length].categoryImage) {
      validateTemp[length].categoryImage = true;
    } else {
      const allTrue = validateTemp.every(
        (obj) => obj.categoryName === false && obj.categoryImage === false,
      );
      if (allTrue) {
        setLoading(true);
        let apiService = new APICallService(
          master.addCategory,
          categoryJSON.addPrimaryCategory({ primaryCategory: primaryCategory }),
          "",
          "",
          "",
          "",
          Categories,
          selectedWarehouse?.value || null,
        );
        let response = await apiService.callAPI();
        if (response) {
          success(masterToast.addPrimaryCategory);
          navigate("/UProcure/master/categories");
        }
      }
    }
    setValidation(validateTemp);
    setLoading(false);
  };
  return (
    <>
      <div className="p-5">
        <Row>
          <Col lg={12} className="mb-6">
            <h1 className="fs-22 fw-bolder">Add primary category</h1>
          </Col>
          <Col>
            {primaryCategory.map((val, index) => {
              return (
                <>
                  <div className="border border-r10px bg-light position-relative mb-6 p-5">
                    <Row>
                      <Col lg={6} className="mb-6">
                        <Row className="align-items-center">
                          <Col lg={4}>
                            <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3">
                              Category name
                            </label>
                          </Col>
                          <Col lg={8}>
                            <input
                              type="text"
                              className={clsx(
                                "form-control form-control-solid bg-white h-60px border form-control-lg text-dark fs-16 fw-600",
                                validation[index].categoryName
                                  ? "border-danger"
                                  : "border",
                              )}
                              name="name"
                              placeholder="Type here…"
                              disabled={loading}
                              value={val.categoryName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ): void =>
                                handleCategoryName(e.target.value, index)
                              }
                            />
                            <div className="fv-plugins-message-container">
                              <span className="text-danger fs-12 fw-bold">
                                {validation[index].categoryName
                                  ? "Please enter category name"
                                  : ""}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col lg={6} className="mb-6 ">
                        {" "}
                        <Row className="align-items-center">
                          <Col lg={4} className="align-self-center">
                            <label className="fs-16 fw-500">
                              Is the category related to tobacco?
                            </label>
                          </Col>
                          <Col
                            lg={2}
                            className="align-self-center d-lg-flex d-md-flex"
                          >
                            <div className="form-check form-check-custom form-check-solid me-4">
                              <input
                                className="form-check-input "
                                type="radio"
                                value="0"
                                id="flexRadioChecked"
                                checked={!val.isTobacco}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ): void => handleTobacco(e.target.value, index)}
                                disabled={loading}
                              />
                              <label className="form-check-label fs-16 fw-600 text-dark">
                                No
                              </label>
                            </div>
                            <div className="form-check form-check-custom form-check-solid">
                              <input
                                className="form-check-input"
                                type="radio"
                                value="1"
                                id="flexRadioChecked"
                                checked={val.isTobacco}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ): void => handleTobacco(e.target.value, index)}
                                disabled={loading}
                              />
                              <label className="form-check-label fs-16 fw-600 text-dark">
                                Yes
                              </label>
                            </div>
                          </Col>{" "}
                        </Row>
                      </Col>
                      <Col
                        lg={6}
                        className="mb-6"
                      >
                        <Row className="align-items-center">
                          <Col lg={4}>
                            <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3">
                              Is Pharmacy Category
                            </label>
                          </Col>
                          <Col lg={8} className="d-flex align-items-center gap-3">
                            <span className="fs-16 fw-600 text-dark">Off</span>
                            <CustomToggleSwitch
                              checked={val.isPharmacyCategory}
                              onChange={(value) => handlePharmacyToggle(value, index)}
                              disabled={loading}
                              size="md"
                            />
                            <span className="fs-16 fw-600 text-dark">On</span>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    {primaryCategory.length == 1 ? (
                      <></>
                    ) : !loading ? (
                      <div className="group-categories-cross">
                        <img
                          src={cross}
                          height={16}
                          width={16}
                          onClick={(e) => {
                            handleRemove(e, index);
                          }}
                          alt=""
                        />
                      </div>
                    ) : (
                      <></>
                    )}
                    <Row>
                      <Col lg={2}>
                        <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3 mt-lg-3">
                          Upload category image
                        </label>
                      </Col>
                      <Col lg={10} className="d-flex">
                        <div className="d-flex flex-column">
                          <div className="upload-btn-wrapper">
                            <div className="symbol symbol-160px symbol-md-170px">
                              <img
                                className={clsx(
                                  "border border-r10px bg-white",
                                  validation[index].categoryImage
                                    ? "border-danger"
                                    : "",
                                )}
                                src={
                                  val.categoryImage
                                    ? val.categoryImage
                                    : UploadImage
                                }
                                alt=""
                              />
                            </div>
                            <input
                              className="w-100 h-100"
                              type="file"
                              name="myfile"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ): void => handleImageChange(e, index)}
                              disabled={loading}
                            />
                            <div className="fv-plugins-message-container">
                              <span className="text-danger fs-12 fw-bold">
                                {validation[index].categoryImage
                                  ? "Please upload category image"
                                  : ""}
                              </span>
                            </div>
                          </div>
                          <span className="text-gray text-italic">{`Image dimensions:-(700x700px)`}</span>
                        </div>
                        {val.categoryImage && !loading ? (
                          <label
                            className="btn btn-icon btn-circle btn-active-color-primary w-50px h-50px responsive-closed"
                            data-kt-image-input-action="change"
                            title=""
                          >
                            <img
                              src={Close}
                              onClick={(): void => handleImageDiscard(index)}
                              className="responsive-closed-icon-closed"
                              alt=""
                              height={33}
                              width={33}
                            />
                          </label>
                        ) : (
                          <></>
                        )}
                        <div className="d-flex flex-column ms-4 justify-content-end">
                          <span className="fs-14 fw-500 text-italic">
                            Note: Please upload a high resolution PNG image
                          </span>
                          <span className="fs-14 fw-500 text-italic">
                            (No background) for the category image.
                          </span>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </>
              );
            })}
          </Col>
        </Row>
        <div className="d-flex flex-column">
          <Link
            to="#"
            className="text-primary fs-16 fw-bolder"
            onClick={handleAddMore}
          >
            + Add new category
          </Link>
          <Row className="align-items-center mt-6 g-3 ">
            <Col xs="auto">
              <Button
                variant="danger"
                size="lg"
                className="uw-btn uw-btn-danger uw-btn-lg btn-active-danger"
                onClick={() => {
                  navigate("/UProcure/master/categories");
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              <Button
                variant="primary"
                size="lg"
                className="uw-btn uw-btn-primary uw-btn-lg w-fit-content"
                onClick={handleSaveCategory}
                disabled={
                  loading ||
                  !validation.every(
                    (obj) =>
                      obj.categoryName === false && obj.categoryImage === false,
                  )
                }
              >
                {!loading && (
                  <span className="indicator-label fs-16 fw-bold">
                    Save category
                  </span>
                )}
                {loading && (
                  <span
                    className="indicator-progress fs-16 fw-bold"
                    style={{ display: "block" }}
                  >
                    Please wait...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};
export default AddPrimaryCategory;
